/**
 * ChatBubble — persistent floating action button for the AI agent.
 *
 * Renders at bottom-right of every page. Click expands ChatDrawer as a
 * slide-over panel. Chat state (WebSocket, messages) persists across
 * navigation because ChatContext is mounted at AppShell level.
 *
 * On mobile (<lg): expands to full-screen overlay.
 * On desktop (>=lg): expands to 400px right panel.
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X } from 'lucide-react'
import { ChatInterface } from './ChatInterface'
import { useChat } from '@/contexts/ChatContext'

export function ChatBubble() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem('chat-bubble-open') === 'true'
    } catch { return false }
  })
  const { messages, isConnected } = useChat()
  const [lastSeenCount, setLastSeenCount] = useState(messages.length)

  const unreadCount = open ? 0 : Math.max(0, messages.filter(m => m.type === 'bot').length - lastSeenCount)

  useEffect(() => {
    try {
      localStorage.setItem('chat-bubble-open', String(open))
    } catch { /* noop */ }
    if (open) {
      setLastSeenCount(messages.filter(m => m.type === 'bot').length)
    }
  }, [open, messages])

  return (
    <>
      {/* Backdrop — mobile only */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-hairline bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900
                        bottom-0 right-0 h-full w-full
                        lg:bottom-20 lg:right-6 lg:h-[calc(100vh-160px)] lg:max-h-[700px] lg:w-[400px] lg:rounded-2xl"
          >
            {/* Panel header */}
            <div className="flex items-center justify-between border-b border-hairline px-4 py-3 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-butler-600 text-white text-sm">
                  🤖
                </div>
                <div>
                  <span className="text-sm font-semibold text-ink dark:text-white">Agent</span>
                  <span className="ml-2 text-xs text-ink-soft">
                    {isConnected ? '● Online' : '○ Offline'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="rounded-lg p-1.5 text-ink-soft hover:bg-surface-page hover:text-ink dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Chat body */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              <ChatInterface />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB — always visible when panel is closed */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="chat-fab"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Open chat"
            className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-butler-600 text-white shadow-lg hover:bg-butler-700 focus:outline-none focus:ring-2 focus:ring-butler-400 focus:ring-offset-2 transition-colors"
          >
            <MessageCircle className="h-6 w-6" />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-marquee-500 text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  )
}
